import {Component, ElementRef, EventEmitter, Input, OnInit, Output} from "@angular/core";

export interface CheckboxListItem {
  display:String,
  isChecked:boolean,
  data?:any,
  isNested:boolean,
  isPrimary:boolean
}

@Component({
  standalone: false,
  selector: "checkbox-list",
  templateUrl: "./checkbox-list.component.html",
  styleUrls: ["./checkbox-list.component.scss"]
})
export class CheckboxListComponent implements OnInit {

  /** Incremented per component instance so ids are unique document-wide. */
  private static instanceCount = 0;

  /**
   * Unique per-instance id prefix. Guarantees each checkbox id (and the
   * `label[for]` bound to it) is unique even when several checkbox-list
   * instances render in the same document, so the visible label stays
   * programmatically associated with its own control and Dragon can target it
   * by name (WCAG 4.1.1 Parsing / 4.1.2 Name, Role, Value).
   */
  readonly idPrefix = `checkbox-list-${CheckboxListComponent.instanceCount++}`;

  @Input() items:CheckboxListItem[];

  @Output() itemChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(private readonly el: ElementRef<HTMLElement>) { }

  ngOnInit() {
  }

  // Roving Up/Down (plus Home/End) arrow navigation for the parameter list.
  // The rows are native checkboxes, so Tab already reaches each one, but a
  // vertical option list is also expected to move focus with the arrow keys
  // (WAI-ARIA list pattern / WCAG 2.1.1 Keyboard). This handler moves focus to
  // the checkbox of the previous/next row (clamped at the ends, no wrap) and
  // works whether focus currently sits on a row's checkbox OR its Y-AXIS
  // toggle, without disturbing the existing Space-to-toggle behaviour.
  onListKeydown(event: KeyboardEvent) {
    const key = event.key;

    // Left/Right move focus horizontally within a row, between the parameter
    // checkbox on the left and its Y-AXIS toggle on the right, so keyboard
    // users can reach the toggle without leaving the arrow-key model
    // (WCAG 2.1.1 Keyboard).
    if (key === "ArrowRight" || key === "ArrowLeft") {
      this.moveHorizontalFocus(event, key);
      return;
    }

    if (key !== "ArrowDown" && key !== "ArrowUp" && key !== "Home" && key !== "End") {
      return;
    }

    const rows = Array.from(
      this.el.nativeElement.querySelectorAll<HTMLElement>(".checkbox-list-item")
    );
    if (rows.length === 0) {
      return;
    }

    const activeRow = (document.activeElement as HTMLElement | null)?.closest(".checkbox-list-item") ?? null;
    const currentIndex = activeRow ? rows.indexOf(activeRow as HTMLElement) : -1;

    let nextIndex: number;
    switch (key) {
      case "ArrowDown":
        nextIndex = currentIndex < 0 ? 0 : Math.min(currentIndex + 1, rows.length - 1);
        break;
      case "ArrowUp":
        nextIndex = currentIndex < 0 ? 0 : Math.max(currentIndex - 1, 0);
        break;
      case "Home":
        nextIndex = 0;
        break;
      default: // "End"
        nextIndex = rows.length - 1;
        break;
    }

    const target = rows[nextIndex]?.querySelector<HTMLInputElement>('input[type="checkbox"]');
    if (target) {
      event.preventDefault();
      target.focus();
    }
  }

  // Left/Right arrow navigation WITHIN a row. The parameter checkbox and the
  // Y-AXIS toggle sit side by side, so ArrowRight moves focus from the checkbox
  // to the toggle and ArrowLeft moves it back. The toggle only exists / is
  // visible when the row is checked (it is `display:none` otherwise), so we
  // guard on it actually being rendered before moving focus onto it.
  private moveHorizontalFocus(event: KeyboardEvent, key: string) {
    const active = document.activeElement as HTMLElement | null;
    const row = active?.closest<HTMLElement>(".checkbox-list-item") ?? null;
    if (!row) {
      return;
    }

    // The parameter checkbox is the row's direct child input; the toggle's own
    // input (also a checkbox with role="switch") lives inside <p-toggleswitch>.
    const checkbox = row.querySelector<HTMLInputElement>(':scope > input[type="checkbox"]');
    const toggle = row.querySelector<HTMLElement>("p-toggleswitch .p-toggleswitch-input");
    const toggleVisible = !!toggle && toggle.offsetParent !== null;

    if (key === "ArrowRight" && active === checkbox && toggleVisible) {
      event.preventDefault();
      toggle.focus();
    } else if (key === "ArrowLeft" && toggleVisible && active === toggle && checkbox) {
      event.preventDefault();
      checkbox.focus();
    }
  }

  // Emit when the checkbox's own state changes. Binding to the native `change`
  // event (with `[(ngModel)]` owning `isChecked`) makes the parameter apply
  // reliably for mouse, keyboard (Space) AND assistive tech such as Dragon —
  // which toggles the control's checked state rather than firing the bubbling
  // click the old handler relied on (WCAG 2.1.1 Keyboard / 4.1.2 Name, Role,
  // Value).
  onItemToggle(item){
    this.itemChanged.emit(item);
  }
  handlePrimary(item){
    item.isPrimary=!item.isPrimary
    this.itemChanged.emit(item);
  }
}
